/**
 * What this learner has going with support, kept fresh enough to be useful
 * and cheap enough to be forgettable.
 *
 * `lib/support.ts` is the three calls; this is the one thing in the tab that
 * makes them. It is modelled on `useChatInbox` rather than on `useTeams`,
 * and the difference between those two is the whole argument for the shape
 * here. A team invitation arrives in hours and nobody is waiting on it, so
 * teams ask only when somebody looks. A support answer is the other thing
 * entirely: the learner asked a question and is waiting for a reply, and a
 * panel that only finds out about it when the panel is opened is a panel
 * that tells them nothing until they go looking — which is the moment they
 * had already decided nobody was coming.
 *
 * So this polls, on the inbox's cadence, and the header button is what the
 * poll is for: it can say *a reply is waiting* without the learner having to
 * hold the room open.
 *
 * ## The four guards, all of them borrowed
 *
 * 1. **At most one read per 60 s** unless something forced it. The same
 *    number `useChatInbox` uses, for the same reason — it is slow enough to
 *    be free and fast enough that nobody sits in front of a stale panel.
 * 2. **Forced on a change of account, and on coming back to the tab.** Those
 *    are the two moments the answer on screen is most likely to be wrong and
 *    the two the learner is most likely to be looking at it.
 * 3. **An `AbortController` and a generation counter, together.** Either one
 *    alone leaks: aborting does not stop a response that has already been
 *    parsed from being applied, and a counter alone leaves the request out
 *    there. Two people share a browser, one signs out, the other signs in,
 *    and the first one's conversation must not land in the second one's
 *    panel.
 * 4. **Every failure is `NO_SUPPORT`.** That is `lib/inbox.ts`'s rule 1 and
 *    it is binding: nothing about support may break the chat. A Worker that
 *    has never heard of `/api/support`, an expired session, an empty roster
 *    and no network are four facts and one answer — no door — and the panel
 *    draws the conversation either way.
 *
 * The collapse is `lib/support.ts`'s doing rather than this file's: every
 * call there already returns the neutral answer instead of throwing, so
 * there is no `catch` here to forget to write.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupportTopic } from '@engineer-os/chat';
import {
  NO_SUPPORT,
  fetchSupport,
  openSupport,
  resolveSupport,
  type OpenOutcome,
  type SupportSnapshot,
} from '../lib/support';

/**
 * The snapshot, spread, plus the three things a caller can do about it.
 *
 * Spread rather than nested for `TeamsApi`'s reason: the component reads
 * `support.thread` and `support.enabled` on nearly every line, and a panel
 * that has to write `support.snapshot.thread` everywhere grows a local alias
 * in each component to avoid it.
 */
export interface SupportApi extends SupportSnapshot {
  /** True while a read is out. Only ever used to say "loading" instead of "no". */
  loading: boolean;
  /**
   * Ask again. Forced by default, because the callers are a person opening
   * the popover and a write that just changed the answer, and both of those
   * are worth a request whatever the clock says.
   */
  refresh(force?: boolean): void;
  open(topic: SupportTopic, subject?: string): Promise<OpenOutcome>;
  resolve(): Promise<boolean>;
}

/** `useChatInbox`'s cadence, and deliberately the same number. */
const EVERY = 60_000;

/**
 * @param enabled Whether there is an account to ask about. Signed out this
 * hook makes no requests at all: every route answers 404 without a session,
 * so the only thing a read could establish is that the reader is still
 * signed out, which the caller already knew.
 */
export function useSupport(enabled: boolean): SupportApi {
  const [snapshot, setSnapshot] = useState<SupportSnapshot>(NO_SUPPORT);
  const [loading, setLoading] = useState(false);
  // Read inside the async body, where `enabled` from the closure would be
  // the value it had when the callback was made rather than the value it has
  // when the answer arrives.
  const live = useRef(enabled);
  live.current = enabled;
  const generation = useRef(0);
  const request = useRef<AbortController | null>(null);
  const lastRead = useRef(0);

  const pull = useCallback(async (force = false) => {
    if (!live.current) return;
    if (!force && Date.now() - lastRead.current < EVERY) return;
    // Stamped before the request rather than after it, so a slow answer does
    // not become licence to send the next one early.
    lastRead.current = Date.now();
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const gen = generation.current;
    setLoading(true);
    const snap = await fetchSupport(controller.signal);
    // Four questions, and each one rules out a different way of being late:
    // this request was cancelled, a newer one replaced it, the account
    // changed under it, or the reader signed out while it was in flight.
    if (controller.signal.aborted || request.current !== controller || gen !== generation.current || !live.current) return;
    setSnapshot(snap);
    setLoading(false);
  }, []);

  const refresh = useCallback(
    (force = true) => {
      void pull(force);
    },
    [pull],
  );

  /**
   * A change of account empties the panel before it asks anything.
   *
   * Emptying first matters more here than it does for an inbox: what is on
   * screen is one person's conversation with support, and leaving it up
   * while the next person's answer is fetched would show them somebody
   * else's subject line for as long as the request takes.
   */
  useEffect(() => {
    generation.current += 1;
    lastRead.current = 0;
    setSnapshot(NO_SUPPORT);
    setLoading(false);
    if (enabled) void pull(true);
    return () => {
      generation.current += 1;
      request.current?.abort();
    };
  }, [enabled, pull]);

  /**
   * The timer, and coming back to the tab.
   *
   * Neither runs while signed out, and the timer skips a hidden document
   * rather than firing into one: a tab left open in the background for a day
   * would otherwise spend fourteen hundred requests finding out nothing,
   * and the `visibilitychange` below covers the only moment that work would
   * have been worth anything — the moment somebody looks again.
   */
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void pull(true);
    };
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void pull(false);
    }, EVERY);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, pull]);

  /**
   * Start a conversation, and show it immediately.
   *
   * The thread goes into the snapshot from the answer rather than from the
   * refresh that follows, because the next thing that happens is the panel
   * opening the room — and a panel that has to wait a round trip to find out
   * which room would either open nothing or open it a second late. The
   * refresh behind it is for the rest of the snapshot: how many people are
   * at the desk, and whether anything moved into `recent`.
   *
   * `enabled` is set with it, and that is not optimism. The Worker has just
   * accepted a thread, which is the only thing `enabled` claims.
   */
  const open = useCallback(
    async (topic: SupportTopic, subject?: string): Promise<OpenOutcome> => {
      const gen = generation.current;
      const done = await openSupport(topic, subject);
      if (gen !== generation.current) return done;
      if (done.ok) {
        const { thread } = done;
        setSnapshot((s) => ({ ...s, enabled: true, thread }));
        void pull(true);
      }
      return done;
    },
    [pull],
  );

  /**
   * Close it from this end.
   *
   * Moved into `recent` here rather than waited for, because the learner has
   * just said they are done and the transcript should still be one press
   * away. Nothing is lost if the refresh disagrees: the server's answer
   * replaces this one a moment later, and writing in the room reopens the
   * thread anyway.
   */
  const resolve = useCallback(async (): Promise<boolean> => {
    const gen = generation.current;
    const done = await resolveSupport();
    if (gen !== generation.current) return done;
    if (done) {
      setSnapshot((s) =>
        s.thread === null ? s : { ...s, thread: null, recent: [{ ...s.thread, state: 'resolved' }, ...s.recent] },
      );
      void pull(true);
    }
    return done;
  }, [pull]);

  return { ...snapshot, loading, refresh, open, resolve };
}
